import {Button, Stack, Flash, Dialog} from '@primer/react'
import {StopIcon} from '@primer/octicons-react'
import {useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useClosePullRequestMutation} from '../../hooks/mutations/use-close-pull-request-mutation'
import {useAnalytics} from '@github-ui/use-analytics'

export function ClosePullRequestSection() {
  const [dialogState, setDialogState] = useState<'closed' | 'open'>('closed')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {sendAnalyticsEvent} = useAnalytics()

  const onOpenCloseDialog = () => {
    setDialogState('open')
    setErrorMessage(null)
  }

  const {mutate: closePullRequestMutation, isPending} = useClosePullRequestMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      setTimeout(() => announce('Failed to close the pull request'), 1000)
    },
    onSuccess: () => {
      setDialogState('closed')
      setTimeout(() => announce('The pull request was successfully closed.'), 1000)
      sendAnalyticsEvent('close_pull_request_section.close', 'MERGEBOX_CLOSE_PULL_REQUEST_SECTION_CLOSE_BUTTON')
    },
  })

  const handleClosePullRequest = () => {
    if (isPending) return
    setErrorMessage(null)
    closePullRequestMutation()
  }

  const handleDismiss = () => {
    if (!isPending) setDialogState('closed')
  }

  return (
    <>
      <Stack direction="horizontal" align="baseline" className="text-small flex-justify-start tmp-px-3 py-1" gap="none">
        <span className="fgColor-muted pr-1">Not planning to merge?</span>
        <Button variant="link" className="Link--muted Link--inTextBlock text-normal" onClick={onOpenCloseDialog}>
          Close pull request
        </Button>
      </Stack>

      {dialogState === 'open' && (
        <Dialog
          title="Close this pull request?"
          role="alertdialog"
          onClose={handleDismiss}
          footerButtons={[
            {content: 'Cancel', buttonType: 'default', onClick: handleDismiss, disabled: isPending, autoFocus: true},
            {
              content: 'Close pull request',
              buttonType: 'danger',
              onClick: handleClosePullRequest,
              loading: isPending,
              loadingAnnouncement: 'Closing pull request',
            },
          ]}
        >
          <Flash variant="warning">This pull request will be closed. You can reopen it later if needed.</Flash>

          {errorMessage && (
            <Flash className="my-2" variant="danger">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
        </Dialog>
      )}
    </>
  )
}
